import { useState } from "react";
import { api } from "../../lib/api";
import { X, LifeBuoy, Loader2, Upload, CheckCircle2, Trash2 } from "lucide-react";

const ISSUE_TYPES = [
  { key: "document_upload", label: "Document Upload Issue" },
  { key: "report_download", label: "Report Download Issue" },
  { key: "ai_analysis", label: "Document Analysis Issue" },
];

// Quick support-ticket modal. Founder identity (name / email / account ID) is
// captured server-side from the session — the founder only picks an issue
// type, writes a short description and optionally attaches a screenshot.
export const SupportTicketModal = ({ defaultIssueType, onClose }) => {
  const [issueType, setIssueType] = useState(defaultIssueType || "document_upload");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!description.trim()) { setErr("Please tell us briefly what happened."); return; }
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("issue_type", issueType);
      fd.append("description", description.trim());
      if (file) fd.append("screenshot", file);
      await api.post("/founder/support-ticket", fd, { timeout: 60000 });
      setDone(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't send your request — please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="support-ticket-modal">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-neutral-200 px-6 py-4">
          <div className="flex items-center gap-2">
            <LifeBuoy className="h-4 w-4 text-[#0A8B85]" />
            <h2 className="text-base font-bold text-neutral-900">Get help</h2>
          </div>
          <button onClick={onClose} data-testid="support-ticket-close" aria-label="Close"
            className="rounded p-1 hover:bg-neutral-100"><X className="h-4 w-4" /></button>
        </div>

        {done ? (
          <div className="p-6 text-center" data-testid="support-ticket-success">
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
            <p className="mt-4 text-sm font-semibold leading-relaxed text-neutral-800">
              Your request has been sent to our support team. We will get back to you shortly.
            </p>
            <a href="/founder/support" data-testid="support-ticket-view-tickets"
              className="mt-3 inline-block text-xs font-semibold text-[#0A8B85] hover:underline">
              View my support tickets →
            </a>
            <div>
              <button onClick={onClose} data-testid="support-ticket-done-close"
                className="mt-4 rounded-lg bg-[#0A8B85] px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#066667]">
                Close
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 p-6">
            <div>
              <label className="text-xs font-bold text-neutral-700">Issue type</label>
              <select value={issueType} onChange={(e) => setIssueType(e.target.value)}
                data-testid="support-ticket-issue-type"
                className="mt-1.5 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#0A8B85]">
                {ISSUE_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-neutral-700">Description</label>
              <textarea rows={4} maxLength={300} value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Briefly tell us what happened."
                data-testid="support-ticket-description"
                className="mt-1.5 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-sm leading-relaxed outline-none focus:border-[#0A8B85]" />
              <p className="mt-1 text-right text-[11px] text-neutral-400">{description.length}/300</p>
            </div>
            <div>
              <label className="text-xs font-bold text-neutral-700">Attach a screenshot (optional)</label>
              {file ? (
                <div className="mt-1.5 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm" data-testid="support-ticket-file">
                  <span className="min-w-0 flex-1 truncate font-medium text-neutral-800">{file.name}</span>
                  <button type="button" onClick={() => setFile(null)} aria-label="Remove screenshot"
                    data-testid="support-ticket-file-remove"
                    className="rounded p-1 text-neutral-400 hover:bg-white hover:text-rose-600">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <label className="mt-1.5 flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-neutral-300 bg-neutral-50 px-3 py-2.5 text-sm text-neutral-600 hover:border-[#0A8B85] hover:text-[#0A8B85]" data-testid="support-ticket-file-upload">
                  <Upload className="h-4 w-4" />
                  <span>Choose an image…</span>
                  <input type="file" className="hidden" accept="image/*"
                    data-testid="support-ticket-file-input"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) setFile(f); e.target.value = ""; }} />
                </label>
              )}
            </div>
            {err && <p className="text-sm text-red-600" data-testid="support-ticket-error">{err}</p>}
            <button onClick={submit} disabled={busy}
              data-testid="support-ticket-submit"
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#0A8B85] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#066667] disabled:opacity-50">
              {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : "Send to Support"}
            </button>
            <a href="/founder/support" data-testid="support-ticket-view-existing"
              className="block text-center text-xs font-semibold text-neutral-500 hover:text-[#0A8B85] hover:underline">
              View my existing tickets →
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

// Small "Having trouble? Get help" link that opens the modal, pre-selecting
// the issue type for the area it lives in.
export const GetHelpLink = ({ issueType, className = "", testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
        data-testid={testid || `get-help-${issueType}`}
        className={"inline-flex items-center gap-1.5 text-xs font-semibold text-[#0A8B85] hover:underline " + className}>
        <LifeBuoy className="h-3.5 w-3.5" /> Having trouble? Get help
      </button>
      {open && <SupportTicketModal defaultIssueType={issueType} onClose={() => setOpen(false)} />}
    </>
  );
};
